<script setup lang="ts">
import { AtSign, Mail, Send, UserRound } from "lucide-vue-next";
import { useField, useForm } from "vee-validate";
import * as yup from "yup";

type ContactField = "name" | "email" | "messageType" | "message";

const { t } = useI18n();
const axios = useAxios();
const toast = useToast();

const CONTACT_FIELDS: ContactField[] = [
  "name",
  "email",
  "messageType",
  "message",
];

const touched = reactive<Record<ContactField, boolean>>({
  name: false,
  email: false,
  messageType: false,
  message: false,
});

const formError = ref<string | null>(null);
const isSending = ref(false);

const messageTypes = computed(() => [
  {
    label: t("contact_form.message_types.general"),
    value: "general",
  },
  {
    label: t("contact_form.message_types.professionals"),
    value: "professionals",
  },
  {
    label: t("contact_form.message_types.support"),
    value: "support",
  },
]);

const schema = yup.object({
  name: yup
    .string()
    .required(t("contact_form.form.name.required_error"))
    .min(2, t("contact_form.form.name.min_error")),

  email: yup
    .string()
    .required(t("contact_form.form.email.required_error"))
    .email(t("contact_form.form.email.invalid_error")),

  messageType: yup
    .string()
    .required(t("contact_form.form.message_type.required_error")),

  message: yup
    .string()
    .required(t("contact_form.form.message.required_error"))
    .min(10, t("contact_form.form.message.min_error")),
});

const { errors, validateField, setFieldTouched, meta, resetForm } = useForm({
  validationSchema: schema,
  initialValues: {
    name: "",
    email: "",
    messageType: "",
    message: "",
  },
});

const { value: name } = useField<string>("name");
const { value: email } = useField<string>("email");
const { value: messageType } = useField<string>("messageType");
const { value: message } = useField<string>("message");

const isButtonDisabled = computed(() => {
  if (isSending.value) return true;

  const hasTouchedFields = CONTACT_FIELDS.some(field => touched[field]);

  if (!hasTouchedFields) return false;

  return !meta.value.valid;
});

function getError(field: ContactField) {
  if (!touched[field]) return "";
  return errors.value[field] || "";
}

async function submitForm() {
  let hasErrors = false;
  formError.value = null;

  for (const field of CONTACT_FIELDS) {
    touched[field] = true;
    setFieldTouched(field, true);

    const result = await validateField(field);

    if (!result.valid) {
      hasErrors = true;
    }
  }

  if (hasErrors) return;

  try {
    isSending.value = true;

    await axios.post("/contact", {
      name: name.value,
      email: email.value,
      messageType: messageType.value,
      message: message.value,
    });

    toast.success({
      title: t("toast.contact.success.title"),
      description: t("toast.contact.success.description"),
    });

    resetForm();

    for (const field of CONTACT_FIELDS) {
      touched[field] = false;
    }
  } catch (error) {
    console.error("Contact form failed: ", error);

    const status = (error as { response?: { status?: number } })?.response
      ?.status;

    if (status === 429) {
      formError.value = t("toast.contact.rate_limited.description");

      toast.error({
        title: t("toast.contact.rate_limited.title"),
        description: t("toast.contact.rate_limited.description"),
      });
    } else {
      formError.value = t("common.errors.unexpected");

      toast.error({
        title: t("toast.error.unexpected_error.title"),
        description: t("toast.error.unexpected_error.description"),
      });
    }
  } finally {
    isSending.value = false;
  }
}

watch([name, email, messageType, message], () => {
  if (formError.value) {
    formError.value = null;
  }
});
</script>

<template>
  <section class="w-full bg-white py-12 md:py-16">
    <div class="mx-auto w-full max-w-[1280px] px-4 md:px-6">
      <div
        class="grid gap-9 rounded-2xl border border-black/[0.025] bg-[#F5F8F8] px-5 py-7 md:px-8 md:py-9 lg:grid-cols-[minmax(0,0.75fr)_minmax(520px,1.25fr)] lg:items-start lg:gap-14 lg:px-10"
      >
        <div class="lg:pt-2">
          <h2
            class="font-gabarito text-3xl font-bold leading-none text-app-text md:text-4xl"
          >
            {{ t("contact_form.title") }}
          </h2>

          <p
            class="mt-4 max-w-[430px] text-sm leading-6 text-app-muted md:text-base"
          >
            {{ t("contact_form.subtitle") }}
          </p>

          <a
            href="mailto:info@senta.lt"
            class="group mt-6 inline-flex items-center gap-3 rounded-xl border border-black/[0.045] bg-white px-3.5 py-3 text-sm font-semibold text-primary shadow-[0_6px_18px_rgba(15,23,42,0.035)] transition duration-300 hover:border-primary/20 hover:shadow-[0_8px_22px_rgba(15,23,42,0.05)]"
          >
            <span
              class="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-50 text-primary"
            >
              <Mail class="h-4 w-4" :stroke-width="1.8" />
            </span>
            <span>info@senta.lt</span>
          </a>
        </div>

        <form
          class="rounded-2xl border border-black/[0.06] bg-white p-4 text-left shadow-[0_12px_32px_rgba(15,23,42,0.055)] md:p-5"
          @submit.prevent="submitForm"
        >
          <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <div class="relative">
                <UserRound
                  class="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-app-muted/70"
                  :stroke-width="1.8"
                />
                <input
                  v-model="name"
                  type="text"
                  name="name"
                  autocomplete="name"
                  :placeholder="t('contact_form.form.name.placeholder')"
                  class="h-11 w-full rounded-lg border bg-[#FCFDFD] pl-10 pr-3.5 text-[15px] text-app-text outline-none transition-colors duration-300 placeholder:text-app-placeholder hover:border-black/15"
                  :class="
                    getError('name')
                      ? 'border-app-danger focus:border-app-danger'
                      : 'border-app-border focus:border-primary'
                  "
                />
              </div>
              <p v-if="getError('name')" class="mt-1.5 text-xs text-app-danger">
                {{ getError("name") }}
              </p>
            </div>

            <div>
              <div class="relative">
                <AtSign
                  class="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-app-muted/70"
                  :stroke-width="1.8"
                />
                <input
                  v-model="email"
                  type="email"
                  name="email"
                  autocomplete="email"
                  :placeholder="t('contact_form.form.email.placeholder')"
                  class="h-11 w-full rounded-lg border bg-[#FCFDFD] pl-10 pr-3.5 text-[15px] text-app-text outline-none transition-colors duration-300 placeholder:text-app-placeholder hover:border-black/15"
                  :class="
                    getError('email')
                      ? 'border-app-danger focus:border-app-danger'
                      : 'border-app-border focus:border-primary'
                  "
                />
              </div>
              <p
                v-if="getError('email')"
                class="mt-1.5 text-xs text-app-danger"
              >
                {{ getError("email") }}
              </p>
            </div>
          </div>

          <div class="mt-3">
            <AppSelect
              v-model="messageType"
              :options="messageTypes"
              width="full"
              :placeholder="t('contact_form.form.message_type.placeholder')"
              :sheet-title="t('contact_form.form.message_type.label')"
              value-class="text-app-text text-[15px]"
              placeholder-class="text-app-placeholder text-[15px]"
              :trigger-class="`flex h-11 w-full items-center justify-between gap-3 rounded-lg border bg-[#FCFDFD] px-3.5 text-left text-[15px] font-normal text-app-text transition-colors duration-300 ${getError('messageType') ? 'border-app-danger' : 'border-app-border hover:border-primary'}`"
            />
            <p
              v-if="getError('messageType')"
              class="mt-1.5 text-xs text-app-danger"
            >
              {{ getError("messageType") }}
            </p>
          </div>

          <div class="mt-3">
            <textarea
              v-model="message"
              name="message"
              :placeholder="t('contact_form.form.message.placeholder')"
              rows="5"
              class="min-h-[132px] w-full resize-y rounded-lg border bg-[#FCFDFD] px-3.5 py-3 text-[15px] leading-6 text-app-text outline-none transition-colors duration-300 placeholder:text-app-placeholder hover:border-black/15"
              :class="
                getError('message')
                  ? 'border-app-danger focus:border-app-danger'
                  : 'border-app-border focus:border-primary'
              "
            />
            <p
              v-if="getError('message')"
              class="mt-1.5 text-xs text-app-danger"
            >
              {{ getError("message") }}
            </p>
          </div>

          <p
            v-if="formError"
            class="mt-3 rounded-lg bg-red-50 px-3.5 py-2.5 text-sm text-app-danger"
          >
            {{ formError }}
          </p>

          <div class="mt-4 flex justify-end">
            <button
              type="submit"
              :disabled="isButtonDisabled"
              class="inline-flex min-h-11 w-full select-none items-center justify-center gap-2 rounded-lg bg-primary px-8 text-sm font-semibold text-white shadow-[0_6px_16px_rgba(15,120,101,0.14)] transition duration-300 hover:bg-primary-400 hover:shadow-[0_8px_20px_rgba(15,120,101,0.18)] disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
            >
              <span>{{ t("contact_form.actions.submit") }}</span>
              <Send class="h-4 w-4" :stroke-width="1.8" />
            </button>
          </div>
        </form>
      </div>
    </div>
  </section>
</template>
